import React from "react";
import axios from "axios";
import { Trash2, Edit3, Clock } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function minutesToHours(m) {
    if (!m) return "0h";
    const h = Math.floor(m / 60), mm = m % 60;
    return mm ? `${h}h ${mm}m` : `${h}h`;
}

export default function ShiftListView({ shifts, usersMap, isAdmin, onEdit, onRefresh, token }) {
    const handleDelete = async (id) => {
        if (!window.confirm("Schicht wirklich löschen?")) return;
        try {
            await axios.delete(`${API}/api/shifts/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Schicht gelöscht");
            onRefresh && onRefresh();
        } catch (e) {
            toast.error("Löschen fehlgeschlagen");
        }
    };

    if (shifts.length === 0) {
        return (
            <div className="p-8 text-center text-sm text-neutral-500" data-testid="shift-list-empty">
                Keine Schichten in diesem Zeitraum geplant.
            </div>
        );
    }

    // Group shifts by date for clean list
    const byDate = {};
    shifts.forEach((s) => {
        byDate[s.date] = byDate[s.date] || [];
        byDate[s.date].push(s);
    });
    const sortedDates = Object.keys(byDate).sort();

    return (
        <div className="divide-y divide-neutral-100" data-testid="shift-list-view">
            {sortedDates.map((date) => {
                const items = byDate[date];
                const totalMin = items.reduce((a, b) => a + (b.worked_minutes || 0), 0);
                return (
                    <section key={date} className="p-4">
                        <header className="flex items-baseline justify-between mb-2">
                            <h3 className="font-semibold text-sm">
                                {new Date(date).toLocaleDateString("de-DE", {
                                    weekday: "long",
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                })}
                            </h3>
                            <span className="text-xs text-neutral-500">
                                {items.length} Schichten · {minutesToHours(totalMin)} gesamt
                            </span>
                        </header>
                        <ul className="space-y-1.5">
                            {items.map((s) => {
                                const u = usersMap[s.user_id] || {};
                                return (
                                    <li
                                        key={s.id}
                                        className="flex items-center justify-between gap-3 rounded-md border border-neutral-200 px-3 py-2 hover:bg-neutral-50"
                                        data-testid={`shift-list-item-${s.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm truncate">{u.name || u.email || s.user_id}</div>
                                            <div className="text-xs text-neutral-500 flex items-center gap-1.5 mt-0.5">
                                                <Clock className="w-3 h-3" />
                                                {s.start_time}–{s.end_time}
                                                <span>·</span>
                                                <span>{minutesToHours(s.worked_minutes)}</span>
                                                {s.breaks && s.breaks.length > 0 && (
                                                    <>
                                                        <span>·</span>
                                                        <span>{s.breaks.length} Pause</span>
                                                    </>
                                                )}
                                            </div>
                                            {s.notes && (
                                                <div className="text-xs text-neutral-500 mt-1 truncate italic">
                                                    {s.notes}
                                                </div>
                                            )}
                                        </div>
                                        {isAdmin && (
                                            <div className="flex items-center gap-1 shrink-0">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => onEdit(s)}
                                                    data-testid={`shift-list-edit-${s.id}`}
                                                >
                                                    <Edit3 className="w-3.5 h-3.5" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDelete(s.id)}
                                                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                                    data-testid={`shift-list-delete-${s.id}`}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                );
            })}
        </div>
    );
}
